import { useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  aiContextCleanupLabel,
  aiContextCleanupRequestable,
  aiContextCounts,
  aiContextMatches,
  parseAiContextRows,
  type AiContextFilter,
  type AiContextRow,
} from '../lib/ai-contexts';
import { shortSessionId } from '../lib/ai-sessions';

const FILTERS: readonly { readonly key: AiContextFilter; readonly label: string }[] = [
  { key: 'all', label: 'All shown' },
  { key: 'active', label: 'Active' },
  { key: 'automated', label: 'Automated / Brunel' },
  { key: 'owner', label: 'Owner-created' },
  { key: 'archived', label: 'Archived' },
  { key: 'disposable', label: 'Disposable' },
  { key: 'cleanup', label: 'Cleanup requested / reported' },
];

type ContextRead =
  | { readonly scopeKey: string | null; readonly status: 'loading' | 'unavailable'; readonly rows: readonly AiContextRow[] }
  | { readonly scopeKey: string; readonly status: 'ready'; readonly rows: readonly AiContextRow[] };

export function AiContexts({
  client, orgId, project, ownerVerified, onVerifyOwner,
}: {
  readonly client: SupabaseClient;
  readonly orgId: string;
  readonly project: { readonly id: string; readonly name: string };
  readonly ownerVerified: boolean;
  readonly onVerifyOwner: () => void;
}) {
  const [filter, setFilter] = useState<AiContextFilter>('all');
  const [read, setRead] = useState<ContextRead>({ scopeKey: null, status: 'loading', rows: [] });
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [mutationMessage, setMutationMessage] = useState<string | null>(null);
  const scopeKey = `${orgId}:${project.id}`;
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;

  useEffect(() => {
    setPendingId(null);
    setMutationMessage(null);
  }, [scopeKey]);

  useEffect(() => {
    let current = true;
    setFilter('all');
    setRead({ scopeKey, status: 'loading', rows: [] });
    void Promise.resolve(client.schema('brunel').from('ai_sessions')
      .select('ai_session_id,project_id,worker_number,node_name,creation_source,runtime_key,provider_identity_state,provider_session_id,state,last_seen_at,replaces_ai_session_id,replacement_reason,lineage_acknowledged,disposition,latest_assignment_id,latest_task_id,latest_task_title,task_chain_id,assignment_count,display_title,cleanup_state,cleanup_detail')
      .eq('org_id', orgId).eq('project_id', project.id)
      .order('last_seen_at', { ascending: false }).limit(200))
      .then(({ data, error }) => {
        if (!current) return;
        const rows = error === null ? parseAiContextRows(data, project.id) : null;
        setRead(rows === null
          ? { scopeKey, status: 'unavailable', rows: [] }
          : { scopeKey, status: 'ready', rows });
      })
      .catch(() => {
        if (current) setRead({ scopeKey, status: 'unavailable', rows: [] });
      });
    return () => { current = false; };
  }, [client, orgId, project.id, scopeKey, refreshKey]);

  async function setDisposition(row: AiContextRow) {
    if (!ownerVerified || pendingId !== null || row.project_id !== project.id) return;
    const requestScope = scopeKey;
    const disposition = row.disposition === 'keep' ? 'disposable' : 'keep';
    setPendingId(row.ai_session_id);
    setMutationMessage(null);
    try {
      const { data, error } = await client.rpc('set_ai_session_disposition', {
        request: { orgId, aiSessionId: row.ai_session_id, disposition },
      });
      const reply = data as { ok?: boolean; value?: { aiSessionId?: string; disposition?: string }; error?: { code?: string } } | null;
      if (currentScope.current !== requestScope) return;
      if (error || reply?.ok !== true || reply.value?.aiSessionId !== row.ai_session_id
        || reply.value.disposition !== disposition) {
        setMutationMessage(`Could not change this context (${reply?.error?.code ?? 'unavailable'}).`);
      } else {
        setRefreshKey((key) => key + 1);
      }
    } catch {
      if (currentScope.current === requestScope) setMutationMessage('Could not change this context (unavailable).');
    } finally {
      if (currentScope.current === requestScope) setPendingId(null);
    }
  }

  async function requestCleanup(row: AiContextRow) {
    if (!ownerVerified || pendingId !== null || row.project_id !== project.id || !aiContextCleanupRequestable(row)) return;
    const requestScope = scopeKey;
    setPendingId(row.ai_session_id);
    setMutationMessage(null);
    try {
      const { data, error } = await client.rpc('request_ai_session_cleanup', {
        request: { orgId, aiSessionId: row.ai_session_id },
      });
      const reply = data as { ok?: boolean; value?: { aiSessionId?: string; cleanupState?: string }; error?: { code?: string } } | null;
      if (currentScope.current !== requestScope) return;
      if (error || reply?.ok !== true || reply.value?.aiSessionId !== row.ai_session_id
        || reply.value.cleanupState !== 'requested') {
        setMutationMessage(`Could not request cleanup (${reply?.error?.code ?? 'unavailable'}).`);
      } else {
        setRefreshKey((key) => key + 1);
      }
    } catch {
      if (currentScope.current === requestScope) setMutationMessage('Could not request cleanup (unavailable).');
    } finally {
      if (currentScope.current === requestScope) setPendingId(null);
    }
  }

  return <AiContextsCatalog rows={read.scopeKey === scopeKey ? read.rows : []}
    status={read.scopeKey === scopeKey ? read.status : 'loading'} filter={filter} onFilter={setFilter}
    ownerVerified={ownerVerified} onVerifyOwner={onVerifyOwner} onSetDisposition={setDisposition}
    onRequestCleanup={requestCleanup} pendingId={pendingId} mutationMessage={mutationMessage} />;
}

export function AiContextsCatalog({
  rows, status, filter, onFilter, ownerVerified, onVerifyOwner, onSetDisposition, onRequestCleanup, pendingId, mutationMessage,
}: {
  readonly rows: readonly AiContextRow[];
  readonly status: ContextRead['status'];
  readonly filter: AiContextFilter;
  readonly onFilter: (filter: AiContextFilter) => void;
  readonly ownerVerified?: boolean;
  readonly onVerifyOwner?: () => void;
  readonly onSetDisposition?: (row: AiContextRow) => void;
  readonly onRequestCleanup?: (row: AiContextRow) => void;
  readonly pendingId?: string | null;
  readonly mutationMessage?: string | null;
}) {
  const counts = useMemo(() => aiContextCounts(rows), [rows]);
  const visible = useMemo(() => rows.filter((row) => aiContextMatches(row, filter)), [rows, filter]);

  return (
    <section className="work one engine-room-sessions engine-room-contexts" aria-label="Observed AI contexts">
      <header className="engine-sessions-head">
        <div>
          <h2>Observed AI contexts</h2>
          <p className="muted">Recorded Worker AI contexts within the workforce sessions above.</p>
        </div>
        {onSetDisposition ? <span className="tag">Keep / Disposable and owner cleanup</span> : <span className="tag">Read-only</span>}
      </header>
      {onSetDisposition && !ownerVerified && onVerifyOwner ? (
        <button type="button" onClick={onVerifyOwner}>Verify owner to change Keep / Disposable</button>
      ) : null}
      {mutationMessage ? <p role="status">{mutationMessage}</p> : null}
      {status === 'loading' ? <p className="muted">Reading recorded AI contexts…</p> : null}
      {status === 'unavailable' ? (
        <p role="status">Detailed AI context records are unavailable. The workforce sessions above remain readable.</p>
      ) : null}
      {status === 'ready' ? (
        <>
          <div className="engine-session-filters" role="group" aria-label="AI context filters">
            {FILTERS.map((item) => (
              <button type="button" key={item.key} className={filter === item.key ? 'active' : undefined}
                aria-pressed={filter === item.key} onClick={() => onFilter(item.key)}>
                <span>{item.label}</span><strong>{counts[item.key]}</strong>
              </button>
            ))}
          </div>
          <p className="muted engine-session-boundary">
            Showing up to 200 most recently seen contexts. This catalog does not expose configured or Actual
            model; those remain Unknown. Native cleanup removes only the runtime&apos;s own conversation;
            Brunel&apos;s record, lineage and audit stay.
          </p>
          {visible.length === 0 ? <p className="muted">No recorded AI contexts match this filter.</p> : (
            <ul className="engine-session-list">
              {visible.map((row) => <AiContextCard key={row.ai_session_id} row={row}
                onSetDisposition={ownerVerified ? onSetDisposition : undefined}
                onRequestCleanup={ownerVerified ? onRequestCleanup : undefined} pending={pendingId !== null} />)}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

function AiContextCard({ row, onSetDisposition, onRequestCleanup, pending }: {
  readonly row: AiContextRow;
  readonly onSetDisposition?: (row: AiContextRow) => void;
  readonly onRequestCleanup?: (row: AiContextRow) => void;
  readonly pending?: boolean;
}) {
  const source = row.creation_source === 'brunel_automated' ? 'Brunel automated'
    : row.creation_source === 'brunel_adopted' ? 'Brunel adopted'
      : row.creation_source === 'owner_interactive' ? 'Owner-created' : 'Imported';
  const seen = new Date(row.last_seen_at);
  return (
    <li>
      <article className="engine-session-card" data-lifecycle={row.state}>
        <header>
          <span><strong>{row.display_title}</strong>
            <small className="muted">Worker {row.worker_number} · {row.node_name} · {source}</small>
          </span>
          <span className="status">{row.state.replaceAll('_', ' ')}</span>
        </header>
        <dl>
          <div><dt>Context</dt><dd><code title={row.ai_session_id}>{shortSessionId(row.ai_session_id)}</code></dd></div>
          <div><dt>Runtime</dt><dd><code>{row.runtime_key}</code></dd></div>
          <div><dt>Configured model</dt><dd>Unknown</dd></div>
          <div><dt>Configured effort</dt><dd>Unknown</dd></div>
          <div><dt>Actual model</dt><dd>Unknown</dd></div>
          <div><dt>Provider-native ID</dt><dd>{row.provider_identity_state === 'exact' && row.provider_session_id !== null
            ? <code title={row.provider_session_id}>{shortSessionId(row.provider_session_id)}</code> : 'Unknown'}</dd></div>
          <div><dt>Run</dt><dd>Unknown</dd></div>
          <div><dt>Task</dt><dd>{row.latest_task_id === null ? 'Unknown' : <><code title={row.latest_task_id}>{shortSessionId(row.latest_task_id)}</code>{row.latest_task_title === null ? '' : ` · ${row.latest_task_title}`}</>}</dd></div>
          <div><dt>Task chain</dt><dd>{row.task_chain_id === null ? 'Unknown' : <code title={row.task_chain_id}>{shortSessionId(row.task_chain_id)}</code>}</dd></div>
          <div><dt>Assignments</dt><dd>{row.assignment_count}</dd></div>
          <div><dt>Replaces</dt><dd>{row.replaces_ai_session_id === null ? 'None recorded'
            : <><code title={row.replaces_ai_session_id}>{shortSessionId(row.replaces_ai_session_id)}</code> · {row.replacement_reason ?? 'Unknown'} · {row.lineage_acknowledged === true ? 'Acknowledged' : 'Unacknowledged'}</>}</dd></div>
          <div><dt>Keep / disposable</dt><dd>{row.disposition === 'keep' ? 'Keep' : 'Disposable'}</dd></div>
          <div><dt>Native cleanup</dt><dd>{aiContextCleanupLabel(row.cleanup_state)}
            {row.cleanup_detail === null ? '' : ` · ${row.cleanup_detail}`}</dd></div>
          <div><dt>Last seen</dt><dd>{Number.isNaN(seen.getTime()) ? 'Unknown' : seen.toLocaleString()}</dd></div>
        </dl>
        {onSetDisposition ? <button type="button" disabled={pending} onClick={() => onSetDisposition(row)}>
          {row.disposition === 'keep' ? 'Mark Disposable' : 'Keep this context'}
        </button> : null}
        {onRequestCleanup && aiContextCleanupRequestable(row) ? <button type="button" disabled={pending}
          onClick={() => onRequestCleanup(row)}>
          {row.cleanup_state === 'failed' ? 'Retry native cleanup' : 'Request native cleanup'}
        </button> : null}
      </article>
    </li>
  );
}
